/**
 * Le domande di Aida, viste da Marina.
 *
 * Fino al 29-08-2026 vivevano solo in `/admin/agenti/aida`, cioè dove Marina non
 * entra: Aida chiedeva e nessuno poteva risponderle se non per email. Enrico ha
 * posto il problema al contrario giusto — se le domande servono ad addestrarla,
 * chi la addestra deve poterle vedere.
 *
 * Tre differenze deliberate rispetto alla vista admin:
 *
 *  - **niente `dati_plico`** (peso lordo, targa, certificato): non insegnano
 *    niente ad Aida, sono dati che servono a generare i documenti e vivono sul
 *    carico dove si generano. Al 29-08 sarebbero 5 voci su 21 in una coda che
 *    deve restare corta per essere credibile;
 *  - **si risponde a parole**, e la risposta passa dallo stesso percorso delle
 *    email: se contiene un dato del tabellone, quello finisce sulla riga;
 *  - **si può archiviare**. Una domanda su un carico partito o su una riga che
 *    non esiste più non ha risposta, e senza un modo di chiuderla la lista non
 *    si svuota mai — a quel punto non la guarda più nessuno.
 *
 * ## Il giro del 12-09-2026 (richiesta di Enrico)
 *
 * - **la più recente in cima**, al contrario di `richieste.aperte()` che ordina
 *   dalla più vecchia. Quella regola («la coda si smaltisce dalla più vecchia»)
 *   resta giusta per la chiusura automatica e per l'admin, ma qui Marina apre la
 *   pagina per vedere *cos'è successo oggi*. L'anzianità non si perde: le
 *   domande da 14 giorni in su restano marcate in ambra;
 * - **una ricerca** su cliente, riferimento, testo e tipo. Il testo è il campo
 *   che conta: cercare «pallet» o «ritiro» raggruppa le domande per *che cosa*
 *   chiedono, che è come si smaltisce una coda fatta di gesti uguali;
 * - **l'archivio**, perché l'archiviazione era a senso unico e invisibile — l'11
 *   settembre Marina ne ha archiviate 62 in un giorno e di quelle 62 non restava
 *   traccia in nessuna pagina. Archiviate e risolte stanno in due sezioni
 *   separate: sono due colonne diverse in DB (`chiusa_il` / `risolto_il`) perché
 *   «non aveva più senso» e «ho risposto» non vanno confuse;
 * - **la doppia conferma**, che non c'era: il cestino archiviava al primo clic, e
 *   «Archivia quelle superate» ne chiudeva N in un colpo senza chiedere niente.
 *   Il modale è anche il posto dove scegliere il *motivo*, che il backend
 *   accettava già e la UI non ha mai usato.
 *
 * ## Il giro del 23-09-2026: le domande coincidono coi badge (scelta B di Enrico)
 *
 * Delle 44 domande aperte in prod solo **3** erano su una riga del tabellone: le
 * altre erano email che Aida non aveva saputo agganciare («Mi manca il
 * riferimento», «Fuori perimetro»), o domande su righe partite e archiviate.
 * Enrico: *«lasciare solamente quelle che hanno un riscontro con le righe della
 * tabella»*, e le email senza riga in una sezione a parte. Quindi:
 *
 * - **Aperte** = solo le domande su una riga viva, lo stesso numero del filtro
 *   «Con domande» del tabellone;
 * - **Email da smistare** = la posta senza riga, fuori dal conteggio;
 * - le domande su righe partite o archiviate si chiudono da sole all'apertura
 *   della pagina («Chiusa dal sistema» in archivio).
 *
 * ## Il giro del 30-09-2026: la scheda
 *
 * Ogni domanda è una **scheda** (`components/m3/SchedaDomanda.tsx`): di chi è la
 * conversazione, cosa Aida ha capito, cosa le manca, come rispondere — con le
 * tre uscite sempre presenti — e il **resoconto** dell'ultima risposta. Nascono
 * due sezioni: **Sospese** («non lo so ancora») e **Indirizzi**, la rubrica di
 * chi è chi, dove si vede cosa Aida ha imparato.
 */

import { useMemo, useState } from "react";
import {
  Archive,
  BookUser,
  Check,
  Clock,
  Inbox,
  Mail,
  RotateCcw,
  Search,
  Trash2,
  Undo2,
  Wand2,
  X,
} from "lucide-react";

import { ChiE } from "@/components/m3/ChiE";
import { ContestoEmail, ETICHETTA_TIPO, SchedaDomanda } from "@/components/m3/SchedaDomanda";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useAnnullaRisposta,
  useArchivioDomande,
  useDomandeAida,
  useDomandeSospese,
  useEmailDaSmistare,
  usePulisciDomande,
  useRiapriDomanda,
  useRubrica,
  useTogliIndirizzo,
} from "@/hooks/useM3";
import { useMe } from "@/hooks/useMe";
import { useMyPermessi } from "@/hooks/usePermessi";
import { isMaitimeStaff } from "@/lib/nav-config";
import { isAzioneConcessa } from "@/lib/permessi";
import { cn } from "@/lib/utils";
import type { DomandaAida, DomandaArchiviata, VoceRubrica } from "@/types/api";

type SezioneId = "aperte" | "sospese" | "smistare" | "archivio" | "indirizzi";

const ETICHETTA_MOTIVO: Record<string, string> = {
  superata: "Superata",
  non_pertinente: "Non era da chiedere",
  automatica: "Chiusa dal sistema",
  informativa: "Non era una domanda",
};

const ETICHETTA_PERCHE: Record<string, string> = {
  era_collega: "era un collega",
  era_spedizioniere: "era uno spedizioniere",
  era_fornitore: "era un fornitore",
  info_nella_mail: "l'informazione era nella mail",
  non_riguarda_spedizioni: "non riguarda le spedizioni",
  altro: "altro",
};

/**
 * La ricerca su una domanda: cliente · riferimento · testo · tipo · interlocutori.
 *
 * Il **testo** è quello che la rende utile davvero — è l'unico campo che dice
 * cosa Aida ha chiesto. Gli interlocutori (30-09) servono per «di questo
 * indirizzo cosa mi ha chiesto?».
 */
function cercaDomande<T extends DomandaAida>(items: T[], q: string): T[] {
  const t = q.trim().toLowerCase();
  if (!t) return items;
  return items.filter((d) =>
    [
      d.cliente ?? "",
      d.riferimento ?? "",
      d.testo,
      d.testo_domanda ?? "",
      d.spiegazione ?? "",
      d.email?.oggetto ?? "",
      ETICHETTA_TIPO[d.tipo] ?? d.tipo,
      ...d.controparte.map((i) => `${i.email} ${i.cliente_nome ?? ""} ${i.trasportatore_nome ?? ""}`),
    ]
      .join(" ")
      .toLowerCase()
      .includes(t),
  );
}

function piuRecentiPrima<T extends DomandaAida>(items: T[] | undefined): T[] {
  return [...(items ?? [])].sort((a, b) => b.creato_il.localeCompare(a.creato_il));
}

export function DomandeView({
  onApriChat,
}: {
  onApriChat?: (d: DomandaAida, testoPerLaChat: string) => void;
}) {
  const [sezione, setSezione] = useState<SezioneId>("aperte");
  const [q, setQ] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  // L'ultima risposta data, per l'«Annulla» del messaggio: chi ha cliccato
  // per sbaglio deve poter tornare indietro dal posto dove lo vede (30-09).
  const [annullabile, setAnnullabile] = useState<{ domandaId: string; tentativoId: string } | null>(null);
  const [confermaPulizia, setConfermaPulizia] = useState(false);

  const { data: me } = useMe();
  const { data: perms } = useMyPermessi();
  // La rubrica è un elenco di indirizzi dei clienti: la stessa porta della
  // corrispondenza (regola del 30-08).
  const vedeRubrica =
    (me ? isMaitimeStaff(me) : false) || isAzioneConcessa(perms, "m3_conversazioni_vedi");

  const { data, isLoading } = useDomandeAida();
  // Si caricano subito: il loro numero deve stare sul pulsante.
  const smistare = useEmailDaSmistare();
  const sospese = useDomandeSospese();
  // L'archivio e la rubrica solo quando si aprono.
  const archivio = useArchivioDomande(sezione === "archivio");
  const rubrica = useRubrica(sezione === "indirizzi" && vedeRubrica);
  const pulisci = usePulisciDomande();

  const domande = useMemo(() => cercaDomande(piuRecentiPrima(data), q), [data, q]);
  const email = useMemo(() => cercaDomande(piuRecentiPrima(smistare.data), q), [smistare.data, q]);
  const inPausa = useMemo(() => cercaDomande(piuRecentiPrima(sospese.data), q), [sospese.data, q]);
  const archiviate = useMemo(
    () => cercaDomande(archivio.data ?? [], q).filter((d) => d.esito === "archiviata"),
    [archivio.data, q],
  );
  const risolte = useMemo(
    () => cercaDomande(archivio.data ?? [], q).filter((d) => d.esito === "risolta"),
    [archivio.data, q],
  );

  if (isLoading) return <Skeleton className="h-64 w-full" />;

  const nAperte = data?.length ?? 0;
  const nSmistare = smistare.data?.length ?? 0;
  const filtrando = q.trim().length > 0;

  function fatto(m: string, a?: { domandaId: string; tentativoId: string }) {
    setMsg(m);
    setAnnullabile(a ?? null);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Sezione icona={Inbox} label="Aperte" valore={nAperte} attiva={sezione === "aperte"} onClick={() => setSezione("aperte")} />
        <Sezione icona={Clock} label="Sospese" valore={sospese.data?.length} attiva={sezione === "sospese"} onClick={() => setSezione("sospese")} />
        <Sezione icona={Mail} label="Email da smistare" valore={smistare.data?.length} attiva={sezione === "smistare"} onClick={() => setSezione("smistare")} />
        <Sezione icona={Archive} label="Archivio" valore={archivio.data?.length} attiva={sezione === "archivio"} onClick={() => setSezione("archivio")} />
        {vedeRubrica && (
          <Sezione icona={BookUser} label="Indirizzi" valore={rubrica.data?.indirizzi.length} attiva={sezione === "indirizzi"} onClick={() => setSezione("indirizzi")} />
        )}
        <div className="relative ml-auto min-w-[16rem] flex-1 sm:max-w-md">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca cliente, indirizzo, commessa, testo della domanda…"
            className="h-[34px] w-full rounded-md border bg-card pl-8 pr-8 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          {filtrando && (
            <button
              type="button"
              onClick={() => setQ("")}
              aria-label="Pulisci la ricerca"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {msg && (
        <Esito
          msg={msg}
          annullabile={annullabile}
          onChiudi={() => {
            setMsg(null);
            setAnnullabile(null);
          }}
          onAnnullata={() => {
            setMsg("Risposta annullata: la domanda è tornata com'era.");
            setAnnullabile(null);
          }}
        />
      )}

      {sezione === "aperte" && (
        <>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              {nAperte === 0
                ? "Nessuna domanda aperta: Aida non ha dubbi in sospeso."
                : filtrando
                  ? `${domande.length} di ${nAperte} ${nAperte === 1 ? "domanda" : "domande"} per «${q.trim()}».`
                  : `${nAperte} ${nAperte === 1 ? "domanda aperta" : "domande aperte"} su righe del tabellone — sono quelle col «?». Ogni risposta dice cosa ha cambiato, e si può annullare.`}
            </p>
            <button
              type="button"
              disabled={pulisci.isPending || nAperte === 0}
              onClick={() => setConfermaPulizia(true)}
              className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
              title="Chiude le domande che non hanno più oggetto"
            >
              <Wand2 className="h-3.5 w-3.5" /> Archivia quelle superate
            </button>
          </div>
          <Elenco
            vuoto={
              nAperte === 0
                ? "Quando ad Aida manca un dato su una riga del tabellone, la domanda compare qui."
                : `Nessuna domanda aperta per «${q.trim()}». Può darsi che sia sospesa o nell'archivio.`
            }
            items={domande}
            onApriChat={onApriChat}
            onFatto={fatto}
          />
        </>
      )}

      {sezione === "sospese" && (
        <>
          <p className="text-sm text-muted-foreground">
            {(sospese.data?.length ?? 0) === 0
              ? "Nessuna domanda sospesa."
              : "Domande a cui hai risposto «non lo so ancora»: tornano da sole alla data indicata, e non accendono il «?» sul tabellone fino ad allora."}
          </p>
          <Elenco vuoto="" items={inPausa} onApriChat={onApriChat} onFatto={fatto} sospese />
        </>
      )}

      {sezione === "smistare" && (
        <>
          <p className="text-sm text-muted-foreground">
            {nSmistare === 0
              ? "Nessuna email da smistare."
              : filtrando
                ? `${email.length} di ${nSmistare} email per «${q.trim()}».`
                : `${nSmistare} email che Aida non ha saputo agganciare a una riga del tabellone. Spesso basta dirle chi è l'interlocutore: la riconosce, e a volte la aggancia da sola.`}
          </p>
          <Elenco vuoto="" items={email} onApriChat={onApriChat} onFatto={fatto} />
        </>
      )}

      {sezione === "archivio" && (
        <ArchivioSezione
          caricamento={archivio.isLoading}
          archiviate={archiviate}
          risolte={risolte}
          q={q}
          onFatto={fatto}
        />
      )}

      {sezione === "indirizzi" && vedeRubrica && (
        <Rubrica
          caricamento={rubrica.isLoading}
          indirizzi={(rubrica.data?.indirizzi ?? []).filter(
            (v) => !filtrando || `${v.email} ${v.cliente_nome ?? ""} ${v.trasportatore_nome ?? ""} ${v.etichetta ?? ""}`.toLowerCase().includes(q.trim().toLowerCase()),
          )}
          spedizionieri={rubrica.data?.spedizionieri ?? []}
          onFatto={fatto}
        />
      )}

      <Dialog open={confermaPulizia} onOpenChange={setConfermaPulizia}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Archiviare le domande superate?</DialogTitle>
            <DialogDescription>
              Vengono archiviate tutte quelle che non hanno più oggetto: la riga del tabellone è
              stata archiviata o non esiste più, oppure il carico è già partito. Le altre restano in
              lista, e le email da smistare non si toccano. Le archiviate si ritrovano nell'archivio
              e si possono riaprire.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setConfermaPulizia(false)}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
            >
              Annulla
            </button>
            <button
              type="button"
              disabled={pulisci.isPending}
              onClick={() =>
                pulisci.mutate(undefined, {
                  onSuccess: (r) => {
                    setConfermaPulizia(false);
                    setMsg(
                      r.chiuse > 0
                        ? `${r.chiuse} archiviate: riguardavano carichi partiti o righe non più in tabella.`
                        : "Nessuna domanda da archiviare.",
                    );
                  },
                })
              }
              className="rounded-md border bg-foreground px-3 py-1.5 text-sm font-medium text-background hover:opacity-90 disabled:opacity-50"
            >
              Archivia
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Il messaggio dopo un gesto, con «Annulla» quando il gesto si può disfare. */
function Esito({
  msg,
  annullabile,
  onChiudi,
  onAnnullata,
}: {
  msg: string;
  annullabile: { domandaId: string; tentativoId: string } | null;
  onChiudi: () => void;
  onAnnullata: () => void;
}) {
  const annulla = useAnnullaRisposta(annullabile?.domandaId ?? "");
  return (
    <p className="sticky top-16 z-20 flex items-start gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 shadow-sm">
      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span className="flex-1">{msg}</span>
      {annullabile && (
        <button
          type="button"
          disabled={annulla.isPending}
          onClick={() => annulla.mutate(annullabile.tentativoId, { onSuccess: onAnnullata })}
          className="inline-flex shrink-0 items-center gap-1 rounded border border-emerald-300 bg-white px-2 py-0.5 font-semibold hover:bg-emerald-100 disabled:opacity-50"
        >
          <Undo2 className="h-3 w-3" /> Annulla
        </button>
      )}
      <button type="button" onClick={onChiudi} aria-label="Chiudi" className="shrink-0">
        <X className="h-3.5 w-3.5" />
      </button>
    </p>
  );
}

function Elenco({
  items,
  vuoto,
  onApriChat,
  onFatto,
  sospese = false,
}: {
  items: DomandaAida[];
  vuoto: string;
  onApriChat?: (d: DomandaAida, testoPerLaChat: string) => void;
  onFatto: (m: string, a?: { domandaId: string; tentativoId: string }) => void;
  sospese?: boolean;
}) {
  if (items.length === 0) {
    return vuoto ? (
      <p className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">{vuoto}</p>
    ) : null;
  }
  return (
    <ul className="space-y-2">
      {items.map((d) => (
        <SchedaDomanda key={d.id} d={d} onApriChat={onApriChat} onFatto={onFatto} sospesa={sospese} />
      ))}
    </ul>
  );
}

/** Una delle sezioni: il numero dice quanto c'è dentro. */
function Sezione({
  icona: Icona,
  label,
  valore,
  attiva,
  onClick,
}: {
  icona: typeof Inbox;
  label: string;
  valore: number | undefined;
  attiva: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium",
        attiva ? "border-foreground bg-foreground text-background" : "hover:bg-muted",
      )}
    >
      <Icona className="h-3.5 w-3.5" />
      {label}
      {valore !== undefined && (
        <span className={cn("tabular-nums", !attiva && "text-muted-foreground")}>{valore}</span>
      )}
    </button>
  );
}

/**
 * L'archivio, in due sottosezioni.
 *
 * **Archiviate** e **risolte** non si mescolano: una domanda archiviata è una
 * domanda buttata via, una risolta ha una risposta che ha cambiato il tabellone.
 * Confonderle vorrebbe dire poter «migliorare» la percentuale di risposte di
 * Aida archiviando — la ragione per cui in DB sono due colonne separate. Dal
 * 30-09 ogni riga dice **come** ne è uscita: il resoconto della risposta, il
 * perché di un «non era da chiedere», o che l'ha risolta Aida da sola.
 */
function ArchivioSezione({
  caricamento,
  archiviate,
  risolte,
  q,
  onFatto,
}: {
  caricamento: boolean;
  archiviate: DomandaArchiviata[];
  risolte: DomandaArchiviata[];
  q: string;
  onFatto: (m: string) => void;
}) {
  if (caricamento) return <Skeleton className="h-64 w-full" />;
  const filtrando = q.trim().length > 0;

  if (archiviate.length === 0 && risolte.length === 0) {
    return (
      <p className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
        {filtrando
          ? `Niente in archivio per «${q.trim()}».`
          : "L'archivio è vuoto: nessuna domanda è stata ancora archiviata o risolta."}
      </p>
    );
  }

  return (
    <div className="space-y-5">
      {risolte.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            {risolte.length} risolte: ognuna dice cosa ha cambiato la risposta.
          </p>
          <ul className="space-y-2">
            {risolte.map((d) => (
              <RigaArchivio key={d.id} d={d} onFatto={onFatto} />
            ))}
          </ul>
        </div>
      )}
      <div className={cn("space-y-2", risolte.length > 0 && "border-t pt-4")}>
        <p className="text-sm text-muted-foreground">
          {archiviate.length === 0
            ? "Nessuna domanda archiviata."
            : `${archiviate.length} archiviate senza risposta. Se una serviva ancora, si riapre.`}
        </p>
        <ul className="space-y-2">
          {archiviate.map((d) => (
            <RigaArchivio key={d.id} d={d} onFatto={onFatto} />
          ))}
        </ul>
      </div>
    </div>
  );
}

/** Una riga d'archivio: la stessa domanda, più com'è finita. */
function RigaArchivio({ d, onFatto }: { d: DomandaArchiviata; onFatto: (m: string) => void }) {
  const riapri = useRiapriDomanda();
  const annulla = useAnnullaRisposta(d.id);
  const quando = new Date(d.conclusa_il).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const ultimo = d.tentativi.filter((t) => !t.annullato_il).at(-1);
  const etichetta =
    d.esito === "risolta"
      ? d.da_aida
        ? "Risolta da Aida"
        : d.completezza === "negativa"
          ? "Risolta: non c'è"
          : "Risolta"
      : (ETICHETTA_MOTIVO[d.motivo ?? ""] ?? "Archiviata");

  return (
    <li className="rounded-lg border bg-muted/30 p-3" data-domanda={d.id}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
            <span className="rounded-full border px-1.5 py-0.5 font-medium">
              {ETICHETTA_TIPO[d.tipo] ?? d.tipo}
            </span>
            {d.riferimento && <span className="font-medium text-foreground">{d.riferimento}</span>}
            {d.cliente && <span>· {d.cliente}</span>}
            <span
              className={cn(
                "rounded-full px-1.5 py-0.5 font-medium",
                d.esito === "risolta" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600",
              )}
            >
              {etichetta}
              {d.perche && ` · ${ETICHETTA_PERCHE[d.perche] ?? d.perche}`}
            </span>
            <span>
              · {quando}
              {d.chiusa_da ? ` · ${d.chiusa_da}` : ""}
            </span>
          </div>
          <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">
            {d.testo_domanda || d.spiegazione || d.testo}
          </p>
          {ultimo && ultimo.effetti.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-xs">
              {ultimo.effetti.map((e, n) => (
                <li key={n} className="flex items-start gap-1">
                  <Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" /> {e.testo}
                </li>
              ))}
            </ul>
          )}
          {d.nota_chiusura && (
            <p className="mt-1 text-xs italic text-muted-foreground">«{d.nota_chiusura}»</p>
          )}
          <ContestoEmail email={d.email} />
        </div>
        {/* Una risolta non si «riapre»: si annulla la risposta che l'ha chiusa,
            con i suoi effetti — vale anche per quelle che Aida ha chiuso da sola
            col ricalcolo, ed è il modo di dirle che aveva sbagliato. */}
        {d.esito === "risolta" && ultimo?.annullabile && (
          <button
            type="button"
            disabled={annulla.isPending}
            onClick={() =>
              annulla.mutate(ultimo.id, {
                onSuccess: () => onFatto("Risposta annullata: la domanda è tornata in lista, com'era."),
              })
            }
            title="Disfa la risposta e i suoi effetti"
            className="inline-flex shrink-0 items-center gap-1 rounded-md border bg-card px-2 py-1 text-[11px] font-medium hover:bg-muted disabled:opacity-50"
          >
            <Undo2 className="h-3 w-3" /> Annulla
          </button>
        )}
        {d.esito === "archiviata" && (
          <button
            type="button"
            disabled={riapri.isPending}
            onClick={() =>
              riapri.mutate(d.id, { onSuccess: () => onFatto("Domanda riaperta: è tornata in lista.") })
            }
            title="Rimettila fra le domande aperte"
            className="inline-flex shrink-0 items-center gap-1 rounded-md border bg-card px-2 py-1 text-[11px] font-medium hover:bg-muted disabled:opacity-50"
          >
            <RotateCcw className="h-3 w-3" /> Riapri
          </button>
        )}
      </div>
    </li>
  );
}

const RUOLO_LABEL: Record<VoceRubrica["ruolo"], string> = {
  cliente: "Cliente",
  spedizioniere: "Spedizioniere",
  fornitore: "Fornitore",
  collega: "Collega",
  altro: "Altro",
};

/**
 * Chi è chi: la rubrica (30-09-2026).
 *
 * È il posto dove si vede cosa Aida ha imparato dalle risposte — gli indirizzi
 * dichiarati, e per ogni spedizioniere i clienti per cui lavora, con quante volte
 * l'associazione è stata vista e se Marina l'ha confermata.
 */
function Rubrica({
  caricamento,
  indirizzi,
  spedizionieri,
  onFatto,
}: {
  caricamento: boolean;
  indirizzi: VoceRubrica[];
  spedizionieri: { trasportatore_id: string; nome: string; email: string[]; clienti: { cliente_codice: number; cliente_nome: string; fonte: string; evidenze: number }[] }[];
  onFatto: (m: string) => void;
}) {
  const togli = useTogliIndirizzo();
  const [modifica, setModifica] = useState<string | null>(null);
  if (caricamento) return <Skeleton className="h-64 w-full" />;
  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          {indirizzi.length === 0
            ? "Nessun indirizzo dichiarato: si aggiungono rispondendo «Chi è?» sulle domande."
            : `${indirizzi.length} indirizzi dichiarati. Qui c'è quello che hai insegnato ad Aida su chi è chi.`}
        </p>
        {indirizzi.length > 0 && (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-2.5 py-2 font-medium">Indirizzo</th>
                  <th className="px-2.5 py-2 font-medium">Chi è</th>
                  <th className="px-2.5 py-2 font-medium">Fonte</th>
                  <th className="px-2.5 py-2" />
                </tr>
              </thead>
              <tbody>
                {indirizzi.map((v) => (
                  <tr key={v.email} className="border-b align-top">
                    <td className="px-2.5 py-2 font-medium">{v.email}</td>
                    <td className="px-2.5 py-2 text-xs">
                      <b>{RUOLO_LABEL[v.ruolo]}</b>
                      {(v.cliente_nome || v.trasportatore_nome || v.etichetta) &&
                        ` · ${v.cliente_nome ?? v.trasportatore_nome ?? v.etichetta}`}
                      {modifica === v.email && (
                        <ChiE
                          email={v.email}
                          ruoloIniziale={v.ruolo}
                          onAnnulla={() => setModifica(null)}
                          onFatto={(m) => {
                            setModifica(null);
                            onFatto(m.length ? `Fatto: ${m.join(" · ")}.` : "Salvato.");
                          }}
                        />
                      )}
                    </td>
                    <td className="px-2.5 py-2 text-xs text-muted-foreground">
                      {v.fonte === "confermata" ? "detto da te" : "imparato"} ·{" "}
                      {new Date(v.aggiornato_il).toLocaleDateString("it-IT")}
                    </td>
                    <td className="whitespace-nowrap px-2.5 py-2 text-right text-[11px]">
                      <button
                        type="button"
                        onClick={() => setModifica(modifica === v.email ? null : v.email)}
                        className="mr-2 text-muted-foreground underline underline-offset-2 hover:text-foreground"
                      >
                        cambia
                      </button>
                      <button
                        type="button"
                        disabled={togli.isPending}
                        onClick={() => togli.mutate(v.email, { onSuccess: () => onFatto(`${v.email} tolto dalla rubrica.`) })}
                        className="inline-flex items-center text-muted-foreground hover:text-red-600"
                        title="Togli dalla rubrica"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {spedizionieri.length > 0 && (
        <div className="space-y-2 border-t pt-4">
          <p className="text-sm text-muted-foreground">
            Spedizionieri e clienti per cui lavorano. Uno spedizioniere serve spesso più clienti: Aida
            lo usa per restringere, e riconosce il cliente da solo solo quando è uno.
          </p>
          <ul className="space-y-2">
            {spedizionieri.map((s) => (
              <li key={s.trasportatore_id} className="rounded-lg border bg-card p-3 text-xs">
                <div className="font-semibold">{s.nome}</div>
                {s.email.length > 0 && <div className="text-muted-foreground">{s.email.join(" · ")}</div>}
                <ul className="mt-1 flex flex-wrap gap-1.5">
                  {s.clienti.map((c) => (
                    <li
                      key={c.cliente_codice}
                      className={cn(
                        "rounded-full border px-2 py-0.5",
                        c.fonte === "confermata" ? "border-emerald-300 bg-emerald-50" : "bg-muted/40",
                      )}
                      title={c.fonte === "confermata" ? "confermato da te" : `visto in ${c.evidenze} email`}
                    >
                      {c.cliente_nome}
                      <span className="ml-1 text-muted-foreground">
                        {c.fonte === "confermata" ? "✓" : `×${c.evidenze}`}
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
